import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button as MuiButton,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
  Divider,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  ExitToApp as LogoutIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { AppSidebar, Layout, StatusBadge, StyledCard } from './common';
import { useAuth } from '../contexts/AuthContext';

interface CustomerData {
  name: string;
  email: string;
  phone: string;
  userId: string;
  createdAt: string;
  isActive: boolean;
}

const CustomerProfile: React.FC = () => {
  const { currentUser, logout, username, isAdmin } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [customerData, setCustomerData] = useState<CustomerData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    loadCustomerData();
  }, []);

  const loadCustomerData = () => {
    try {
      const stored = localStorage.getItem('customerData');
      if (stored) {
        const data = JSON.parse(stored);
        setCustomerData(data);
        setName(data.name || '');
        setEmail(data.email || '');
        setPhone(data.phone || '');
      }
    } catch (error) {
      console.error('Error loading customer data:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !email.trim()) {
      setError('Name and email are required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const newCustomerData: CustomerData = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        userId: currentUser?.uid || '',
        createdAt: customerData?.createdAt || new Date().toISOString(),
        isActive: true
      };

      localStorage.setItem('customerData', JSON.stringify(newCustomerData));
      setCustomerData(newCustomerData);
      setSuccess('Customer profile updated successfully!');
      setIsEditing(false);
      
    } catch (error: any) {
      console.error('Error updating customer profile:', error);
      setError('Failed to update customer profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');

    try {
      localStorage.removeItem('customerData');
      setCustomerData(null);
      setName('');
      setEmail('');
      setPhone('');
      setSuccess('Customer profile deleted successfully!');
      setDeleteDialogOpen(false);
      
    } catch (error: any) {
      console.error('Error deleting customer profile:', error);
      setError('Failed to delete customer profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError('');
    loadCustomerData(); // Reset form
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const drawerWidth = 280;

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToAdminDirectory = () => {
    navigate('/admin', { state: { adminView: 'directory' } });
  };

  const goToAdminManagement = () => {
    navigate('/admin', { state: { adminView: 'management', managementTab: 0 } });
  };

  if (!customerData && !isEditing) {
    return (
      <Layout
        title="Customer Profile"
        username={username || undefined}
        onLogout={logout}
        onProfile={() => navigate('/profile')}
      >
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch' }}>
            <Box
              component="nav"
              sx={{
                width: { md: sidebarCollapsed ? 76 : drawerWidth },
                flexShrink: { md: 0 },
              }}
              aria-label="Sidebar navigation"
            >
              <AppSidebar
                title="Account"
                drawerWidth={drawerWidth}
                collapsedWidth={76}
                collapsed={sidebarCollapsed}
                onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
                mobileOpen={mobileNavOpen}
                onMobileClose={() => setMobileNavOpen(false)}
                topItems={[
                  ...(isAdmin
                    ? [
                        {
                          key: 'admin-directory',
                          label: 'Customer Directory',
                          icon: <DashboardIcon />,
                          selected: false,
                          onClick: goToAdminDirectory,
                        },
                        {
                          key: 'admin-management',
                          label: 'Customer Management',
                          icon: <SettingsIcon />,
                          selected: false,
                          onClick: goToAdminManagement,
                        },
                      ]
                    : [
                        {
                          key: 'dashboard',
                          label: 'Dashboard',
                          icon: <DashboardIcon />,
                          selected: false,
                          onClick: goToDashboard,
                        },
                      ]),
                ]}
                bottomItems={[
                  {
                    key: 'profile',
                    label: 'Profile',
                    icon: <PersonIcon />,
                    selected: true,
                    onClick: () => navigate('/profile'),
                  },
                  {
                    key: 'logout',
                    label: 'Logout',
                    icon: <LogoutIcon />,
                    color: 'error',
                    onClick: logout,
                  },
                ]}
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-start', mb: 2 }}>
                <MuiButton variant="outlined" onClick={() => setMobileNavOpen(true)}>
                  Menu
                </MuiButton>
              </Box>
          <StyledCard sx={{ textAlign: 'center', py: 6 }}>
            <Avatar
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 3,
                bgcolor: 'primary.main',
                fontSize: '2rem',
              }}
            >
              <PersonIcon sx={{ fontSize: '4rem' }} />
            </Avatar>
            
            <Typography variant="h4" gutterBottom>
              No Customer Profile
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              You haven't set up your customer profile yet. Add yourself as a customer to get started with the chatbot platform.
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              startIcon={<PersonIcon />}
              onClick={() => setIsEditing(true)}
              sx={{ px: 4, py: 1.5 }}
            >
              Create Customer Profile
            </Button>
          </StyledCard>
            </Box>
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout
      title="Customer Profile"
      username={username || undefined}
      onLogout={logout}
      onProfile={() => navigate('/profile')}
    >
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch' }}>
          <Box
            component="nav"
            sx={{
              width: { md: sidebarCollapsed ? 76 : drawerWidth },
              flexShrink: { md: 0 },
            }}
            aria-label="Sidebar navigation"
          >
            <AppSidebar
              title="Account"
              drawerWidth={drawerWidth}
              collapsedWidth={76}
              collapsed={sidebarCollapsed}
              onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
              mobileOpen={mobileNavOpen}
              onMobileClose={() => setMobileNavOpen(false)}
              topItems={[
                ...(isAdmin
                  ? [
                      {
                        key: 'admin-directory',
                        label: 'Customer Directory',
                        icon: <DashboardIcon />,
                        selected: false,
                        onClick: goToAdminDirectory,
                      },
                      {
                        key: 'admin-management',
                        label: 'Customer Management',
                        icon: <SettingsIcon />,
                        selected: false,
                        onClick: goToAdminManagement,
                      },
                    ]
                  : [
                      {
                        key: 'dashboard',
                        label: 'Dashboard',
                        icon: <DashboardIcon />,
                        selected: false,
                        onClick: goToDashboard,
                      },
                    ]),
              ]}
              bottomItems={[
                {
                  key: 'profile',
                  label: 'Profile',
                  icon: <PersonIcon />,
                  selected: true,
                  onClick: () => navigate('/profile'),
                },
                {
                  key: 'logout',
                  label: 'Logout',
                  icon: <LogoutIcon />,
                  color: 'error',
                  onClick: logout,
                },
              ]}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-start', mb: 2 }}>
              <MuiButton variant="outlined" onClick={() => setMobileNavOpen(true)}>
                Menu
              </MuiButton>
            </Box>
        {/* Success/Error Messages */}
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
        >
          <Alert
            onClose={() => setSuccess('')}
            severity="success"
            variant="filled"
          >
            {success}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
        >
          <Alert
            onClose={() => setError('')}
            severity="error"
            variant="filled"
          >
            {error}
          </Alert>
        </Snackbar>

        {/* Profile Card */}
        <StyledCard>
          {!isEditing ? (
            <>
              {/* Profile Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    mr: 3,
                    fontSize: '1.5rem',
                  }}
                >
                  {customerData?.name ? getInitials(customerData.name) : <PersonIcon />}
                </Avatar>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" gutterBottom>
                    {customerData?.name}
                  </Typography>
                  <StatusBadge 
                    status={customerData?.isActive ? 'active' : 'inactive'} 
                  />
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Profile
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Profile Information */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      Email Address
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {customerData?.email}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      Phone Number
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {customerData?.phone || 'Not provided'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarIcon sx={{ mr: 2, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                      Customer Since
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {customerData?.createdAt ? new Date(customerData.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'Unknown'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            <>
              {/* Edit Form */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                  {customerData ? 'Edit Profile' : 'Create Profile'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Update your customer information
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Enter your full name"
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Enter your email address"
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={loading}
                  placeholder="Enter your phone number (optional)"
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancelEdit}
                    disabled={loading}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  >
                    {loading ? 'Saving...' : 'Save Profile'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </StyledCard>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Delete Customer Profile
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete your customer profile? This action cannot be undone and will remove all associated data.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              color="error" 
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              {loading ? 'Deleting...' : 'Delete Profile'}
            </Button>
          </DialogActions>
        </Dialog>
          </Box>
        </Box>
      </Container>
    </Layout>
  );
};

export default CustomerProfile; 